import {
    isNothingMatched,
    NOTHING_MATCHED_OUTCOME,
    nothingMatchedMessage,
} from './watchlistImportOutcome';

export const WATCHLIST_LIFECYCLE_CONTRACT_VERSION = 'watchlist-operation-lifecycle.v1';

const PHASES_BY_STATUS = Object.freeze({
    pending_review: new Set(['review']),
    queued: new Set(['msi']),
    running: new Set([
        'msi', 'market_registration', 'factor_data', 'publishing', 'removal_review',
    ]),
    succeeded: new Set(['complete']),
    failed: new Set(['complete']),
    cancelled: new Set(['complete']),
    stale: new Set(['complete']),
    superseded: new Set(['complete']),
    unchanged: new Set(['complete']),
});
const SYNTHETIC_STATUSES = new Set(['pending_review', 'superseded', 'unchanged']);
const TERMINAL_STATUSES = new Set([
    'succeeded', 'failed', 'cancelled', 'stale', 'superseded', 'unchanged',
]);
const SUCCESS_STATUSES = new Set(['succeeded', 'unchanged']);
const FAILURE_STATUSES = new Set(['failed', 'cancelled', 'stale', 'superseded']);
const DELTA_CLASSIFICATIONS = new Set([
    'membership_changed',
    'categorization_changed',
    'custom_metadata_changed',
    'no_change',
]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FACTOR_CONTEXT_FIELDS = Object.freeze([
    'factor_context_id', 'setting_version', 'setting_digest', 'provider_vendor',
    'provider_source_system', 'provider_namespace', 'factor_model',
    'factor_horizon', 'factor_kind', 'factor_release_id', 'factor_deployment_id',
    'provider_source_release_id', 'msi_snapshot_version', 'route_policy_version',
    'factor_as_of_date', 'route_policy_contract', 'guarded_contract_version',
]);

function text(value) {
    return value == null ? '' : String(value).trim();
}

function uuid(value, field, required = true) {
    const normalized = text(value);
    if (!normalized && !required) return null;
    if (!UUID_RE.test(normalized)) throw new Error(`${field} must be a UUID.`);
    return normalized.toLowerCase();
}

function positiveInteger(value, field) {
    if (!Number.isInteger(value) || value < 1) {
        throw new Error(`${field} must be a positive integer.`);
    }
    return value;
}

function nonNegativeInteger(value, field) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`${field} must be a non-negative integer.`);
    }
    return value;
}

function boundedNumber(value, field, minimum, maximum) {
    if (typeof value !== 'number' || !Number.isFinite(value)
        || value < minimum || value > maximum) {
        throw new Error(`${field} is outside its allowed range.`);
    }
    return value;
}

function factorContext(candidate) {
    if (!candidate || typeof candidate !== 'object') {
        throw new Error('Watchlist lifecycle factor context is unavailable.');
    }
    if (FACTOR_CONTEXT_FIELDS.some(
        (field) => !Object.prototype.hasOwnProperty.call(candidate, field)
            || candidate[field] == null,
    )) {
        throw new Error('Watchlist lifecycle factor context is incomplete.');
    }
    const context = {
        factor_context_id: uuid(candidate.factor_context_id, 'factor_context.factor_context_id'),
        setting_version: positiveInteger(candidate.setting_version, 'factor_context.setting_version'),
        setting_digest: text(candidate.setting_digest),
        provider_vendor: text(candidate.provider_vendor),
        provider_source_system: text(candidate.provider_source_system),
        provider_namespace: text(candidate.provider_namespace),
        factor_model: text(candidate.factor_model),
        factor_horizon: text(candidate.factor_horizon),
        factor_kind: text(candidate.factor_kind),
        factor_release_id: positiveInteger(candidate.factor_release_id, 'factor_context.factor_release_id'),
        factor_deployment_id: positiveInteger(candidate.factor_deployment_id, 'factor_context.factor_deployment_id'),
        provider_source_release_id: positiveInteger(
            candidate.provider_source_release_id,
            'factor_context.provider_source_release_id',
        ),
        msi_snapshot_version: positiveInteger(
            candidate.msi_snapshot_version,
            'factor_context.msi_snapshot_version',
        ),
        route_policy_version: positiveInteger(
            candidate.route_policy_version,
            'factor_context.route_policy_version',
        ),
        factor_as_of_date: text(candidate.factor_as_of_date),
        route_policy_contract: text(candidate.route_policy_contract),
        guarded_contract_version: text(candidate.guarded_contract_version),
    };
    const requiredText = [
        'setting_digest', 'provider_vendor', 'provider_source_system',
        'provider_namespace', 'factor_model', 'factor_kind', 'factor_as_of_date',
        'route_policy_contract', 'guarded_contract_version',
    ];
    if (requiredText.some((field) => !text(context[field]))) {
        throw new Error('Watchlist lifecycle factor context is incomplete.');
    }
    if (!/^[0-9a-f]{64}$/.test(context.setting_digest)) {
        throw new Error('Watchlist lifecycle factor setting digest is invalid.');
    }
    if (
        context.guarded_contract_version !== 'msi-v2-guarded-factor-route.v4'
        || context.route_policy_version !== 2
        || context.factor_kind !== 'core'
    ) {
        throw new Error('Watchlist lifecycle factor context contract is invalid.');
    }
    const horizon = text(context.factor_horizon);
    const barra = context.provider_vendor === 'barra'
        && context.provider_source_system === 'factor_data_loaders.barra'
        && context.provider_namespace === 'barrid'
        && horizon === ''
        && context.route_policy_contract === 'fdl-barra-route-policy';
    const axioma = context.provider_vendor === 'axioma'
        && context.provider_source_system === 'factor_data_loaders.axioma'
        && context.provider_namespace === 'axioma_id'
        && ['SH', 'MH'].includes(horizon)
        && context.route_policy_contract === 'fdl-axioma-route-policy';
    if (!barra && !axioma) {
        throw new Error('Watchlist lifecycle factor context vendor route is invalid.');
    }
    return { ...context, factor_horizon: horizon };
}

export function normalizeWatchlistLifecycle(payload) {
    const candidate = payload?.lifecycle ?? payload;
    if (!candidate || typeof candidate !== 'object') {
        throw new Error('Watchlist lifecycle is unavailable.');
    }
    if (candidate.contract_version !== WATCHLIST_LIFECYCLE_CONTRACT_VERSION) {
        throw new Error('Watchlist lifecycle contract version is unsupported.');
    }
    const status = text(candidate.status).toLowerCase();
    const phase = text(candidate.phase).toLowerCase();
    if (!PHASES_BY_STATUS[status]?.has(phase)) {
        throw new Error('Watchlist lifecycle status and phase are inconsistent.');
    }
    const operationId = uuid(
        candidate.operation_id,
        'operation_id',
        !SYNTHETIC_STATUSES.has(status),
    );
    const revisionId = uuid(candidate.watchlist_revision_id, 'watchlist_revision_id');
    const revisionSeq = positiveInteger(candidate.revision_seq, 'revision_seq');
    const uploadAttemptId = uuid(candidate.upload_attempt_id, 'upload_attempt_id', false);
    const progress = candidate.progress;
    if (!progress || typeof progress !== 'object') {
        throw new Error('Watchlist lifecycle progress is unavailable.');
    }
    const percent = boundedNumber(progress.percent, 'progress.percent', 0, 100);
    const completedCreditUnits = nonNegativeInteger(
        progress.completed_credit_units, 'progress.completed_credit_units',
    );
    const totalCreditUnits = nonNegativeInteger(
        progress.total_credit_units, 'progress.total_credit_units',
    );
    const candidateCount = nonNegativeInteger(
        progress.candidate_count, 'progress.candidate_count',
    );
    const msiTerminalCount = nonNegativeInteger(
        progress.msi_terminal_count, 'progress.msi_terminal_count',
    );
    const msiSuccessCount = nonNegativeInteger(
        progress.msi_success_count, 'progress.msi_success_count',
    );
    const marketTerminalCount = nonNegativeInteger(
        progress.market_terminal_count, 'progress.market_terminal_count',
    );
    const marketSuccessCount = nonNegativeInteger(
        progress.market_success_count, 'progress.market_success_count',
    );
    const factorTerminalCount = nonNegativeInteger(
        progress.factor_terminal_count, 'progress.factor_terminal_count',
    );
    const factorSuccessCount = nonNegativeInteger(
        progress.factor_success_count, 'progress.factor_success_count',
    );
    const expectedCompletedCredits = msiTerminalCount
        + 2 * (msiTerminalCount - msiSuccessCount)
        + marketTerminalCount
        + (marketTerminalCount - marketSuccessCount)
        + factorTerminalCount;
    if (
        msiSuccessCount > msiTerminalCount
        || msiTerminalCount > candidateCount
        || marketSuccessCount > marketTerminalCount
        || marketTerminalCount > msiSuccessCount
        || factorSuccessCount > factorTerminalCount
        || factorTerminalCount > marketSuccessCount
        || totalCreditUnits !== 3 * candidateCount
        || completedCreditUnits !== expectedCompletedCredits
        || completedCreditUnits > totalCreditUnits
    ) {
        throw new Error('Watchlist lifecycle progress counters are inconsistent.');
    }
    if (SUCCESS_STATUSES.has(status) ? percent !== 100 : percent >= 100) {
        throw new Error('Watchlist lifecycle completion percentage is inconsistent.');
    }
    const terminalReceipt = candidate.terminal_receipt;
    let normalizedTerminalReceipt = null;
    if (TERMINAL_STATUSES.has(status)) {
        if (!terminalReceipt || typeof terminalReceipt !== 'object') {
            throw new Error('Watchlist terminal receipt is unavailable.');
        }
        const completedAt = text(candidate.completed_at);
        const receiptCompletedAt = text(terminalReceipt.completed_at);
        const receiptOperationId = uuid(
            terminalReceipt.operation_id,
            'terminal_receipt.operation_id',
            !SYNTHETIC_STATUSES.has(status),
        );
        const receiptRevisionId = uuid(
            terminalReceipt.watchlist_revision_id,
            'terminal_receipt.watchlist_revision_id',
        );
        const receiptRevisionSeq = positiveInteger(
            terminalReceipt.revision_seq,
            'terminal_receipt.revision_seq',
        );
        if (
            receiptOperationId !== operationId
            || receiptRevisionId !== revisionId
            || receiptRevisionSeq !== revisionSeq
            || !completedAt
            || receiptCompletedAt !== completedAt
        ) {
            throw new Error('Watchlist terminal receipt does not match its lifecycle.');
        }
        const rawClassification = text(terminalReceipt.delta_classification).toLowerCase();
        const classification = rawClassification || null;
        if (classification != null && !DELTA_CLASSIFICATIONS.has(classification)) {
            throw new Error('Watchlist terminal receipt delta classification is invalid.');
        }
        if (SUCCESS_STATUSES.has(status) && !DELTA_CLASSIFICATIONS.has(classification)) {
            throw new Error('Watchlist terminal receipt has no delta classification.');
        }
        const publicationOutcome = text(terminalReceipt.publication_outcome).toLowerCase() || null;
        if (
            publicationOutcome != null
            && (
                publicationOutcome !== NOTHING_MATCHED_OUTCOME
                || status !== 'succeeded'
                || classification !== 'no_change'
            )
        ) {
            throw new Error('Watchlist terminal receipt publication outcome is invalid.');
        }
        const rawPortfolioSync = terminalReceipt.portfolio_sync;
        if (
            rawPortfolioSync != null
            && (
                typeof rawPortfolioSync !== 'object'
                || Array.isArray(rawPortfolioSync)
                || rawPortfolioSync.applied !== true
            )
        ) {
            throw new Error('Watchlist terminal receipt portfolio sync is invalid.');
        }
        if (
            rawPortfolioSync == null
            && terminalReceipt.portfolio_sync_import_run_id != null
        ) {
            throw new Error('Watchlist terminal receipt portfolio sync is inconsistent.');
        }
        if (
            rawPortfolioSync != null
            && !text(terminalReceipt.portfolio_sync_import_run_id)
        ) {
            throw new Error('Watchlist terminal receipt portfolio sync is inconsistent.');
        }
        const portfolioSyncImportRunId = rawPortfolioSync == null
            ? null
            : uuid(
                terminalReceipt.portfolio_sync_import_run_id,
                'terminal_receipt.portfolio_sync_import_run_id',
                true,
            );
        normalizedTerminalReceipt = {
            operation_id: receiptOperationId,
            watchlist_revision_id: receiptRevisionId,
            revision_seq: receiptRevisionSeq,
            completed_at: receiptCompletedAt,
            delta_classification: classification,
            ...(publicationOutcome ? { publication_outcome: publicationOutcome } : {}),
            ...(rawPortfolioSync != null ? {
                portfolio_sync: { applied: true },
                portfolio_sync_import_run_id: portfolioSyncImportRunId,
            } : {}),
        };
    } else if (terminalReceipt != null) {
        throw new Error('Non-terminal Watchlist lifecycle cannot have a terminal receipt.');
    }
    let normalizedFactorContext = null;
    if (!SYNTHETIC_STATUSES.has(status)) {
        normalizedFactorContext = factorContext(candidate.factor_context);
    } else if (candidate.factor_context != null) {
        normalizedFactorContext = factorContext(candidate.factor_context);
    }
    const retry = candidate.retry;
    if (!retry || typeof retry !== 'object' || !['none', 'retry_pending', 'terminal_error'].includes(retry.classification)) {
        throw new Error('Watchlist lifecycle retry classification is unavailable.');
    }
    if (retry.classification === 'retry_pending' && status !== 'running') {
        throw new Error('Watchlist lifecycle retry classification is inconsistent.');
    }
    if (retry.classification === 'retry_pending' && (!retry.retryable || !text(retry.error_code))) {
        throw new Error('Watchlist lifecycle retry reason is unavailable.');
    }
    if (FAILURE_STATUSES.has(status) && retry.classification !== 'terminal_error') {
        throw new Error('Watchlist terminal error classification is unavailable.');
    }
    if (retry.classification === 'terminal_error' && !text(retry.error_code)) {
        throw new Error('Watchlist terminal error reason is unavailable.');
    }
    if (retry.classification === 'terminal_error' && !FAILURE_STATUSES.has(status)) {
        throw new Error('Watchlist terminal error classification is inconsistent.');
    }
    if (retry.retryable != null && typeof retry.retryable !== 'boolean') {
        throw new Error('Watchlist lifecycle retryable value is invalid.');
    }
    if (retry.classification === 'terminal_error' && retry.retryable === true) {
        throw new Error('Watchlist terminal error cannot be retry pending.');
    }
    if (retry.classification === 'none' && (retry.retryable === true || text(retry.error_code))) {
        throw new Error('Watchlist lifecycle retry fields are inconsistent.');
    }
    const retryable = retry.retryable ?? null;
    const errorCode = text(retry.error_code).toLowerCase() || null;
    return {
        contract_version: WATCHLIST_LIFECYCLE_CONTRACT_VERSION,
        status,
        phase,
        operation_id: operationId,
        watchlist_revision_id: revisionId,
        revision_seq: revisionSeq,
        upload_attempt_id: uploadAttemptId,
        progress: {
            percent,
            candidate_count: candidateCount,
            completed_credit_units: completedCreditUnits,
            total_credit_units: totalCreditUnits,
            msi_terminal_count: msiTerminalCount,
            msi_success_count: msiSuccessCount,
            market_terminal_count: marketTerminalCount,
            market_success_count: marketSuccessCount,
            factor_terminal_count: factorTerminalCount,
            factor_success_count: factorSuccessCount,
        },
        terminal_receipt: normalizedTerminalReceipt,
        factor_context: normalizedFactorContext,
        retry: {
            retryable,
            error_code: errorCode,
            classification: retry.classification,
        },
        ...(candidate.diagnostic && typeof candidate.diagnostic === 'object'
            ? { diagnostic: candidate.diagnostic } : {}),
        created_at: candidate.created_at,
        started_at: candidate.started_at,
        completed_at: candidate.completed_at,
    };
}

export function watchlistLifecycleProgressStatus(payload) {
    const lifecycle = normalizeWatchlistLifecycle(payload);
    if (lifecycle.status === 'pending_review' || lifecycle.status === 'unchanged') return null;
    const failed = FAILURE_STATUSES.has(lifecycle.status);
    const cancelled = lifecycle.status === 'cancelled';
    const succeeded = lifecycle.status === 'succeeded';
    const nothingMatched = isNothingMatched(lifecycle);
    const gateProgress = [
        {
            key: 'identity_resolution',
            terminal: lifecycle.progress.msi_terminal_count,
            success: lifecycle.progress.msi_success_count,
            expected: lifecycle.progress.candidate_count,
        },
        {
            key: 'market_registration',
            terminal: lifecycle.progress.market_terminal_count,
            success: lifecycle.progress.market_success_count,
            expected: lifecycle.progress.msi_success_count,
        },
        {
            key: 'factor_coverage',
            terminal: lifecycle.progress.factor_terminal_count,
            success: lifecycle.progress.factor_success_count,
            expected: lifecycle.progress.market_success_count,
        },
    ];
    const terminalFailure = failed && lifecycle.phase === 'complete';
    const firstIncompleteIndex = gateProgress.findIndex(
        (gate) => gate.terminal < gate.expected,
    );
    const failedGateIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : 3;
    const activePhaseIndex = {
        msi: 0,
        market_registration: 1,
        factor_data: 2,
        publishing: 3,
    }[lifecycle.phase] ?? 3;
    const gateState = (gate, index) => {
        if (succeeded) return 'success';
        if (terminalFailure) {
            if (index < failedGateIndex) return 'success';
            if (index === failedGateIndex) return cancelled ? 'partial' : 'failed';
            return 'skipped';
        }
        if (gate.terminal >= gate.expected) return 'success';
        if (index < activePhaseIndex) return 'success';
        if (index === activePhaseIndex) return 'running';
        return 'queued';
    };
    const publishingState = succeeded
        ? 'success'
        : terminalFailure
            ? failedGateIndex === 3
                ? cancelled ? 'partial' : 'failed'
                : 'skipped'
            : activePhaseIndex === 3
                ? 'running'
                : 'queued';
    return {
        enabled: true,
        found: true,
        reason: 'watchlist_lifecycle',
        operationId: lifecycle.operation_id
            ?? `watchlist-revision:${lifecycle.watchlist_revision_id}`,
        revisionId: lifecycle.watchlist_revision_id,
        operationType: 'watchlist_update',
        cancelled,
        topLevelStatus: succeeded
            ? 'success'
            : cancelled ? 'partial' : failed ? 'failed' : 'running',
        phase: lifecycle.phase,
        message: cancelled
            ? 'Watchlist update cancelled. Your current Watchlist was not changed.'
            : lifecycle.status === 'superseded'
                ? 'Watchlist update was superseded. Your current Watchlist was not changed.'
                : failed
                    ? 'Watchlist update failed. Your current Watchlist was not changed.'
                    : nothingMatched
                        ? nothingMatchedMessage({ progress: lifecycle.progress })
                        : succeeded
                            ? 'Watchlist accepted.'
                            : 'Your Watchlist update is processing.',
        messageCode: nothingMatched
            ? 'watchlist_update_nothing_matched'
            : `watchlist_update_${lifecycle.status}`,
        ...(nothingMatched ? { outcome: NOTHING_MATCHED_OUTCOME } : {}),
        summary: {
            row_count: lifecycle.progress.candidate_count,
            completed_count: Math.floor(lifecycle.progress.completed_credit_units / 3),
            completed_credit_units: lifecycle.progress.completed_credit_units,
            total_credit_units: lifecycle.progress.total_credit_units,
        },
        phases: {
            mutation: { status: 'success' },
            identity_resolution: { status: gateState(gateProgress[0], 0) },
            market_registration: { status: gateState(gateProgress[1], 1) },
            factor_coverage: { status: gateState(gateProgress[2], 2) },
            factor_matrix_refresh: { status: publishingState },
            surface_refresh: { status: publishingState },
        },
        requiredPhases: [
            'mutation',
            'identity_resolution',
            'market_registration',
            'factor_coverage',
            'factor_matrix_refresh',
            'surface_refresh',
        ],
        progressPercent: succeeded ? 100 : Math.min(99, lifecycle.progress.percent),
        indeterminate: false,
        updatedAt: (() => {
            const timestamp = Date.parse(
                lifecycle.completed_at || lifecycle.started_at || lifecycle.created_at || '',
            );
            return Number.isFinite(timestamp) ? new Date(timestamp) : null;
        })(),
        expiresAt: null,
    };
}
